import { useEffect, useState } from "react";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppoitmentStatusList } from "@/app/hooks/api_hooks/dayClosing/useAppoitmentStatusList";
import { useLoader } from "@/app/hooks/useLoader";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import { useForm } from "react-hook-form";
import useFormValidations from "@/app/hooks/useFormValidations";
import NumberInput from "@/app/components/ui/Inputs/NumberInput";
import { useClinicCashCollectAPI } from "@/app/hooks/api_hooks/dayClosing/useClinicCashCollectAPI";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import SortageAmountModal from "./SortageAmountModal";
import ClosingStatus from "@/app/components/ui/Reusable/ClosingStatus";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useDayClosingCurrentShift } from "@/app/hooks/api_hooks/dayClosing/useDayClosingCurrentShift";

const CashClosureComponent = () => {
  const { veticEpocToTime } = useUtils();
  const { execute: fetchDayClosingData, closureData } = useAppoitmentStatusList();
  const { execute: fetchCurrentShift, closureData: shift } = useDayClosingCurrentShift();

  const { execute: cashCollectAPI } = useClinicCashCollectAPI();
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);

  const queryParams = new URLSearchParams(location.search);
  const isLoading = useLoader("isClosingDataInProgress");
  const isLoadingCashCollect = useLoader("isCollectAmountDataInProgress");
  const { NO_APPOINTMENTS } = useImageConstants();
  const [initialLoad, setInitialLoad] = useState(false);
  const { forOnlyNumbersWithDecimal } = useFormValidations();
  const [open, setOpen] = useState(false);

  const formHook = useForm<any>({
    mode: "onSubmit",
    defaultValues: {},
  });

  const { handleSubmit, formState } = formHook;

  useEffect(() => {
    fetchCurrentShift(mappedClinicId);
  }, []);

  // ✅ read query params

  const queryClinics = mappedClinicId;
  const time = queryParams.get("t") ?? "";
  const shiftID = queryParams.get("shift") ?? "";

  useEffect(() => {
    if (queryClinics && time && shiftID) {
      fetchDayClosingData(queryClinics, shiftID, "finance", "cash");
    }
  }, [queryClinics, shiftID, time]);

  const handelClick = () => {
    const payloadData = {
      clinic_id: queryClinics,
      todays_cash_amount: parseFloat(formHook.watch("collect_amount")),
      shift: shift?.shift,
      shortage_amount:
        closureData?.todays_cash_amount - parseInt(formHook.watch("collect_amount")) > 0
          ? closureData?.todays_cash_amount - parseInt(formHook.watch("collect_amount"))
          : undefined,
      shortage_reason: formHook.watch("shortage_reason") ? formHook.watch("shortage_reason") : undefined,
    };
    cashCollectAPI("finance", "cash", payloadData).then(({ status }) => {
      if (status) {
        fetchDayClosingData(queryClinics, shift?.shift, "finance", "cash");
        setOpen(false);
        formHook.setValue("collect_amount", 0);
        formHook.setValue("shortage_reason", null);
      }
    });
  };

  const onSubmit = async () => {
    if (parseFloat(formHook.watch(`collect_amount`)) < closureData?.todays_cash_amount) {
      setOpen(true);
    } else {
      parseFloat(formHook.watch(`collect_amount`)) >= 0 && handelClick();
    }
    //
  };

  useEffect(() => {
    if (closureData?.todays_cash_amount && !initialLoad) {
      formHook.setValue("collect_amount", closureData?.todays_cash_amount);
      setInitialLoad(true);
    }
  }, [initialLoad, closureData?.todays_cash_amount]);
  const todaysCashAmount = Number(closureData?.todays_cash_amount || 0);
  const collectedAmount = Number(formHook.watch("collect_amount") || 0);
  const isAmountExceed = collectedAmount > todaysCashAmount;
  const isAmountShort = collectedAmount > 0 && collectedAmount < todaysCashAmount;
  const canSubmit = collectedAmount >= 0 && collectedAmount <= todaysCashAmount;

  return (
    <>
      {isLoading ? (
        <TableSkeletonComponent columnLength={8} />
      ) : (
        <div className="w-full space-y-4">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="inline-block align-bottom  text-left overflow-hidden transform transition-all  w-full"
          >
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center bg-white gap-3 mb-2">
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 bg-white p-2 border border-gray-200 rounded-lg items-center w-full lg:w-[50%] my-2">
                <p className="col-span-1 sm:col-span-2 text-[#1F1F23] font-semibold ">Total Cash Collection</p>
                <p className="col-span-1 sm:col-span-1 text-[#1F1F23] font-semibold text-right sm:text-left sm:pl-3">{closureData?.todays_cash_amount}</p>
                <div className="col-span-2 sm:col-span-2 ">
                  <div className="relative w-full">
                    <NumberInput
                      className="text-xs w-full "
                      inputClassName="!py-2.5 sm:!py-1.5"
                      placeholder="Enter Final Cash Collection"
                      maxLength={6}
                      max={closureData?.todays_cash_amount}
                      register={formHook.register(`collect_amount`, {
                        required: true,
                        ...forOnlyNumbersWithDecimal.validations,
                        validate: (value) => {
                          const trimmedValue = String(value).replace(/^0+/, "") || "0";
                          formHook.setValue(`collect_amount`, trimmedValue);

                          if (parseFloat(trimmedValue) > closureData?.todays_cash_amount) {
                            return `Amount cannot exceed`;
                          }
                          return null;
                        },
                      })}
                      fieldError={formState.errors.collect_amount}
                      errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                    />

                    {parseFloat(formHook.watch(`collect_amount`)) > closureData?.todays_cash_amount && (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#C94A00] text-[10px] bg-[#FFF2EB] px-2 py-[3px] rounded-md font-medium">
                        Amount exceed
                      </span>
                    )}

                    {parseFloat(formHook.watch(`collect_amount`)) > 0 &&
                      parseFloat(formHook.watch(`collect_amount`)) < closureData?.todays_cash_amount && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#C94A00] text-[10px] bg-[#FFF2EB] px-2 py-[3px] rounded-md font-medium">
                          Amount short
                        </span>
                      )}
                  </div>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <button
                    disabled={isLoadingCashCollect}
                    className={`text-xs w-full sm:w-auto rounded-xl px-6 py-2.5 font-semibold transition-all duration-200 flex items-center justify-center gap-2 text-white hover:opacity-90 disabled:opacity-50 ${formHook.watch("collect_amount") >= 0 &&
                      parseFloat(formHook.watch("collect_amount")) <= closureData?.todays_cash_amount
                      ? "bg-black"
                      : "bg-[#939393]"
                      }`}
                  >
                    {isLoadingCashCollect ? <CircleSpinnerLoader h="20" w="50" /> : "Submit"}
                  </button>
                </div>
              </div>
              <div className="flex gap-3 items-center lg:pr-5">
                <p className="text-sm font-medium">Closure status</p>
                <ClosingStatus closure_status={closureData?.closure_status} />
              </div>
            </div>
          </form>
          {closureData?.cash_transactions?.length > 0 ? (
            <div className="rounded-2xl border border-[#ECEEF2] bg-white p-3 shadow-sm">
              <div className="overflow-x-auto rounded-xl">
                <table className="w-full min-w-[900px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                  <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal">
                    <tr>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Client Name</th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Time</th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Collection Cash</th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Collected by</th>
                    </tr>
                  </thead>

                  <tbody className="text-xs">
                    {closureData?.cash_transactions?.map((txn: any, rowIndex: number) => {
                      const isFirst = rowIndex === 0;
                      const isLast = rowIndex === closureData.cash_transactions.length - 1;
                      const isSingle = closureData.cash_transactions.length === 1;

                      return (
                        <tr
                          key={txn.transaction_id}
                          className={`
                        bg-white hover:bg-[#FCFCFD] transition-colors
                        ${isSingle ? "rounded-xl" : ""}
                        ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                        ${isLast && !isSingle ? "rounded-b-xl" : ""}
                      `}
                        >
                          <td
                            className={`
                          p-3 text-xs text-[#1F1F23]
                          ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                          ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                          ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                        `}
                          >
                            {txn?.user_details?.name}
                          </td>
                          <td className="p-3 text-xs text-[#1F1F23]">{veticEpocToTime(txn.created_at)}</td>
                          <td className="p-3 text-xs text-[#1F1F23]">{txn.transaction_amount}</td>
                          <td
                            className={`
                          p-3 text-xs text-[#6C727F]
                          ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                          ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                          ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                        `}
                          >
                            {txn.created_by}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-[#ECEEF2] bg-white p-6 shadow-sm flex justify-center items-center min-h-[360px]">
              <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Transaction Found" />
            </div>
          )}
        </div>
      )}

      {open && (
        <SortageAmountModal
          open={open}
          setOpen={setOpen}
          amount={todaysCashAmount - collectedAmount}
          formHook={formHook}
          handelClick={() => handelClick()}
        />
      )}
    </>
  );
};

export default CashClosureComponent;
